/*
 * @author: wayou
 * @date: 2018-09-04 17:10:14
 * @description:
 * 替代原生图片标签，增加了
 * - 图片加载态
 * - 图片加载失败的回退展示
 * - 延迟加载 （只在支持 `IntersectionObserver` 的环境下才生效）
 */

import React, { PureComponent } from 'react';
import imageApi from '@umu/umu-image-handle';
import { $document } from '@umu/umu-dom';
import { MemoPreImg } from '../memoPreImg/memoPreImg';

import './emg.scss';

export enum EmgFit {
    'default',
    'contain',
    'cover',
}

import LOAD_ERR_IMG from './images/load_err.png';
import LOADING_IMG from './images/loading.png';

export { LOAD_ERR_IMG, LOADING_IMG };

interface IEmgProps {
    src: string;
    className?: string;
    alt?: string;
    title?: string;
    style?: React.CSSProperties;
    fit: EmgFit;

    /**
     * 加载态展示的图片
     */
    loadingImg?: string;

    /**
     * 加载失败展示的图片以标识失败状态，区别于 `fallbackImg`，它看起来是张错误图片
     */
    loadErrImg?: string;

    /**
     * 加载失败时的回退图片以替换正常需要展示的图片，区别于 `loadErrImg`，它看起来是张正常图片
     * 一般是整站的默认占位图片
     */
    fallbackImg?: string;

    onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
    onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
    onContextMenu?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

interface IEmgState {
    isLoading: boolean;
    isLoadSuccess: boolean;
}

const IMAGE_CLASS_NAMES = {
    IMAGE_CLASSNAME: 'emg',
    IMAGE_LOADING: 'emg-loading',
    IMAGE_LOAD_SUCCESS: 'emg-load-success',
    IMAGE_LOAD_ERR: 'emg-load-error',
};

const doc = $document.querySelector('.doc');

class MemoEmg extends PureComponent<IEmgProps, IEmgState> {
    public static defaultProps: Partial<IEmgProps> = {
        className: '',
        alt: '',
        loadingImg: LOADING_IMG,
        loadErrImg: LOAD_ERR_IMG,
        fallbackImg: '',
        fit: EmgFit.default,
    };

    constructor(props: IEmgProps) {
        super(props);

        this.state = {
            isLoading: true,
            isLoadSuccess: false,
        };
    }

    public render() {
        const { alt, title, style, className, fit, onContextMenu } = this.props;
        const { isLoading, isLoadSuccess } = this.state;
        const imgSrc = this.getImgUrl();

        const isDefaultFit = isLoading || fit === EmgFit.default;
        const classNames = this.getClassName(isDefaultFit ? className : '', isLoading, isLoadSuccess);
        const defaultFit = (
            <MemoPreImg>
                <img
                    className={classNames}
                    src={imgSrc}
                    alt={alt}
                    title={title}
                    style={style}
                    onLoad={this.onLoad}
                    onError={this.onError}
                />
            </MemoPreImg>
        );

        if (!isDefaultFit) {
            let height: string | number = 'auto';
            if (doc && this.props.fit === EmgFit.cover) {
                height = Math.floor(doc.clientWidth / 3 - 7);
            }
            const containStyle = {
                backgroundImage: `url('${imageApi.process(imgSrc)}')`,
            };
            return (
                <div className={`emg-fit-${fit}`} style={{ ...containStyle, ...style }} onContextMenu={onContextMenu}>
                    <MemoPreImg>
                        <img
                            src={imgSrc}
                            className="emg"
                            style={{ height }}
                            onLoad={this.onLoad}
                            onError={this.onError}
                        />
                    </MemoPreImg>
                </div>
            );
        }
        return defaultFit;
    }

    private getImgUrl(): string {
        const { src, loadingImg, loadErrImg, fallbackImg } = this.props;
        const { isLoading, isLoadSuccess } = this.state;

        if (isLoading) {
            return loadingImg!;
        } else {
            if (isLoadSuccess) {
                return src;
            } else {
                if (fallbackImg) {
                    return fallbackImg;
                } else {
                    return loadErrImg!;
                }
            }
        }
    }

    private getClassName(className: string = '', isLoading: boolean, isLoadSuccess: boolean): string {
        return [
            IMAGE_CLASS_NAMES.IMAGE_CLASSNAME,
            !!className ? className : '',
            isLoading
                ? IMAGE_CLASS_NAMES.IMAGE_LOADING
                : isLoadSuccess
                ? IMAGE_CLASS_NAMES.IMAGE_LOAD_SUCCESS
                : IMAGE_CLASS_NAMES.IMAGE_LOAD_ERR,
        ].join(' ');
    }

    private onLoad: React.DOMAttributes<HTMLImageElement>['onLoad'] = (event) => {
        const { onLoad } = this.props;

        this.setState({
            isLoading: false,
            isLoadSuccess: true,
        });

        if (onLoad) {
            onLoad(event);
        }
    };

    private onError: React.DOMAttributes<HTMLImageElement>['onError'] = (event) => {
        if (typeof event === 'string') {
            return;
        }

        const { onError } = this.props;
        this.setState({
            isLoading: false,
            isLoadSuccess: false,
        });

        if (onError) {
            onError(event);
        }
    };
}

export { MemoEmg };
